import React, { useEffect, useState } from 'react';
import { css } from 'emotion';
import { theme } from '@weave/theme-original';
import {
  Heading,
  PrimaryButton,
  TextLink,
  useFormField,
  SwitchField,
} from '@weave/design-system';

import { useMerchant } from './use-merchant.hook';
import { DisconnectStripeBtn } from './disconnect-stripe-btn';
import { ResumeStripePayoutBtn } from './resume-stripe-payout-btn';
import { useBnplStatus } from './hooks/use-bnpl-status';
import SunbitLogo from './assets/SunBit-logo.png';
import { obpApi } from '../../apis/payments';
import { useSelector } from 'react-redux';
import { selectCurrentLocation } from '../../redux/actions/location';
import { useAlert } from '@weave/alert-system';
import { Loader } from './atoms';
import { ActiveMerchantStatus } from './active-merchant-status/active-merchant-status.component';

const statusWrapper = css`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
`;

const actionBtnsWrapper = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  & > *:not(:first-child) {
    margin-left: ${theme.spacing(3)};
  }
`;

const titleRow = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing(2)};
`;

const sunbitSignup = css`
  width: 100%;
`;

const sunbitLogo = css`
  width: ${theme.spacing(2.5)};
  margin-right: ${theme.spacing(1)};
`;

const marginReset = css`
  margin: 0;
  &:not(:last-child) {
    margin-right: ${theme.spacing(2)};
  }
`;

const obpToggleWrapper = css`
  margin-top: ${theme.spacing(4)};
  display: flex;
  align-items: stretch;
  gap: ${theme.spacing(2)};
`;

export const PaymentsStatusDisplay = () => {
  const alerts = useAlert();
  const [isLoadingOBP, setIsLoadingOBP] = useState(false);
  const { paymentsUrl } = useMerchant();
  const { showSignupLink, sunbitSignupLink, onboardingStarted } = useBnplStatus();
  const obpToggleProps = useFormField({
    type: 'switch',
  });

  const location = useSelector(selectCurrentLocation);
  const currentLocationId = location?.LocationID;

  const getFeatureLocationSettings = async () => {
    setIsLoadingOBP(true);
    try {
      const response = await obpApi.getPaymentFeatureSettings(
        paymentsUrl,
        currentLocationId
      );
      obpToggleProps.onChange({
        name: 'onlineBillPayEnabled',
        value: response?.settings?.onlineBillPayEnabled,
      });
    } catch (error) {
      alerts.error('Error getting feature settings');
    } finally {
      setIsLoadingOBP(false);
    }
  };

  useEffect(() => {
    getFeatureLocationSettings();
  }, [currentLocationId]);

  const handleObpToggleChange = async () => {
    setIsLoadingOBP(true);
    try {
      const response = await obpApi.setPaymentFeatureSettings(
        paymentsUrl,
        currentLocationId,
        !obpToggleProps.value
      );
      obpToggleProps.onChange({
        name: 'onlineBillPayEnabled',
        value: response?.settings?.onlineBillPayEnabled,
      });
    } catch (error) {
      alerts.error('Error setting Online Bill Pay toggle');
    } finally {
      setIsLoadingOBP(false);
    }
  };

  return (
    <div className={statusWrapper}>
      <div className={titleRow}>
        <Heading className={marginReset} level={2}>
          Active Merchant Account
        </Heading>
        <div className={actionBtnsWrapper}>
          {showSignupLink && (
            <TextLink href={sunbitSignupLink} target="_blank">
              <PrimaryButton className={sunbitSignup}>
                <img src={SunbitLogo} alt="Sunbit Logo" className={sunbitLogo} />
                {!onboardingStarted ? 'Activate Sunbit' : 'View Sunbit Status'}
              </PrimaryButton>
            </TextLink>
          )}
          <ResumeStripePayoutBtn />
          <DisconnectStripeBtn />
        </div>
      </div>
      <ActiveMerchantStatus />
      <div className={obpToggleWrapper}>
        <SwitchField
          {...obpToggleProps}
          name="obp-flag-switcher"
          label="Online Bill Pay"
          onChange={handleObpToggleChange}
          disabled={isLoadingOBP}
        />
        {isLoadingOBP && <Loader width={theme.spacing(2)} height={theme.spacing(2)} />}
      </div>
    </div>
  );
};
